import { Flex, Box, Heading, Text, Badge } from '@chakra-ui/react';
import { PromptInput } from './prompt/PromptInput';
import { RichTextChunk } from '@texas/api/endpoints/chat/richTextTypes';
import { aiAgentApi } from '@texas/api/endpoints/chat/aiAgentApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { aiEvents } from './events';
import { ErrorDetails } from '../shared/alert/ErrorDetails';
import { InlineFile } from '@texas/api/endpoints/chat/messageTypes';
import { SpinnerWithLabel } from '../shared/SpinnerWithLabel';
import { useTranslation } from 'react-i18next';
import { PredefinedPrompts } from './predefined/PredefinedPrompt';
import { fadeInBottomAnimation } from '@texas/resources/animations/animations';
import { Hat } from './Hat';

export function NewChat() {
  const { request, loading, error } = useApiRequest(aiAgentApi.newSession);
  const { t } = useTranslation();

  async function newChat(
    rawText: string,
    chunks: RichTextChunk[],
    files: InlineFile[],
  ) {
    const response = await request({
      chunks,
      fileIds: files.map((x) => x.fileId),
      rawString: rawText,
      threadId: 0,
    });

    if (response.hasError) {
      return;
    }

    aiEvents.newThreadCreatedWithPrompt.dispatch(
      response.data.map((x) => ({
        threadId: x.threadId,
        title: x.title,
        category: x.category,
      })),
    );
  }

  return (
    <Flex
      bg="texas.bg.950"
      _light={{ bg: 'white' }}
      w="full"
      flexDir="column"
      alignItems="center"
      justifyContent="center"
      h="full"
    >
      <Hat />
      <Flex alignItems="center" gap={1} pb={2}>
        <Heading fontSize="lg">{t('ai.chat')}</Heading>
        <Badge
          bg="texas.sand.100"
          color="white"
          border="1px solid"
          borderColor="texas.sand.50"
          _light={{
            bg: 'texas.burntSienna.200',
            borderColor: 'texas.burntSienna.100',
          }}
        >
          {t('user.beta')}
        </Badge>
      </Flex>

      <Text textAlign="center" px={4} opacity={0.5} variant="small">
        {t('ai.disclaimer')}
      </Text>
      {loading ? (
        <Box pt={4} animation={fadeInBottomAnimation()}>
          <SpinnerWithLabel label={t('ai.starting')} />
        </Box>
      ) : (
        <>
          <PromptInput
            multipleFilesSupport={true}
            onSubmit={(chunks, files, rawText) =>
              newChat(rawText, chunks, files)
            }
          />
          <PredefinedPrompts />
        </>
      )}

      {error && <ErrorDetails error={error} />}
    </Flex>
  );
}
